import React, { createContext, useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { PATHS } from "App/router";
import { IUser, IUserAttributes, UserRoles } from "Auth/types";
import { currentUserSelector, logout } from "Auth/state";
import Intercom, { shutdown } from "@intercom/messenger-js-sdk";
import { intercomSettings } from "shared/utils/hooks/useIntercom";

export interface ProfileContextState {
  currentUser: IUser | null;
  userName: string;
  userAttributes: IUserAttributes | null;
  email: string;
  handleLogout: () => void;
  redirectToProfile: () => void;
  isAdmin: boolean;
}

export const profileContextInitState: ProfileContextState = {
  currentUser: null,
  userName: "",
  userAttributes: null,
  email: "",
  handleLogout: () => {},
  redirectToProfile: () => {},
  isAdmin: false,
};

const ProfileContext = createContext(profileContextInitState);

export const ProfileProvider: React.FC = ({ children }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const currentUser = useSelector(currentUserSelector);
  const userAttributes = useMemo(() => currentUser?.attributes, [currentUser]);

  const isAdmin = useMemo(
    () => userAttributes?.userRoles?.includes(UserRoles.ROLE_ADMIN) || false,
    [userAttributes]
  );

  const { firstname, lastname, email } = currentUser?.attributes || {};

  const userName = useMemo(() => `${firstname} ${lastname}`, [firstname, lastname]);

  const handleLogout = () => {
    dispatch(logout());
    history.push(PATHS.HOME);
    shutdown(); // shutdown intercom
    Intercom(intercomSettings); // reset intercom
  };

  const redirectToProfile = () => {
    history.push(PATHS.PROFILE);
  };

  return (
    <ProfileContext.Provider
      value={{
        currentUser,
        userName,
        userAttributes: userAttributes ?? null,
        email: email ?? "",
        handleLogout,
        redirectToProfile,
        isAdmin,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfileContext = () => useContext(ProfileContext);
